/** ADM-09 Tarification : prix de base, groupes, tarifs clients, remises volume, promotions, simulateur (RM-21). tarif.gerer ★. */
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Choice, SelectField, Table, Tabs, TextField, useToast } from '@myron/design-system';
import { Calculator, Plus, Trash2 } from 'lucide-react';
import type { Page, S } from '@myron/contracts';
import { ApiErreur, del, get, post } from '../../../shared/api/client';
import { useSession } from '../../../shared/auth/session';
import { EntetePage, EtatRequete, ErreurFormulaire } from '../../../shared/ui/composants';
import { euros, LIBELLE_CONDITIONNEMENT, pourcentBp } from '../../../shared/ui/format';
import { aujourdhuiIso, useCategories, useClientsSelecteur, useGroupes, useProduitsSelecteur, versCentimes } from './commun';

type Onglet = 'base' | 'groupes' | 'clients' | 'remises' | 'promotions' | 'simulateur';
const CONDS = [{ value: 'PALETTE', label: 'Palette' }, { value: 'CARTON', label: 'Carton' }, { value: 'PACK', label: 'Pack' }];

function useNomProduit() {
  const p = useProduitsSelecteur();
  const map = useMemo(() => new Map((p.data?.donnees ?? []).map((x) => [x.id, `${x.sku} — ${x.libelle}`])), [p.data]);
  return { produits: p.data?.donnees ?? [], nom: (id?: string) => (id ? map.get(id) ?? id.slice(0, 8) : '—') };
}

function ChoixProduit({ valeur, onChange }: { valeur: string; onChange: (v: string) => void }) {
  const { produits } = useNomProduit();
  return <SelectField label="Produit" required value={valeur} onChange={(e) => onChange(e.target.value)} options={[{ value: '', label: 'Choisir…' }, ...produits.map((p) => ({ value: p.id, label: `${p.sku} — ${p.libelle}` }))]} />;
}

/** Les prix propres à un client sont gérés par les accords commerciaux (validation ◆, historique, marge minimale). */
function OngletPrixClients() {
  return (
    <Alert ton="info" titre="Prix négociés : accords commerciaux" action={<Link className="my-btn my-btn--secondaire my-btn--compact" to="/admin/accords">Ouvrir les accords commerciaux</Link>}>
      Les prix et quantités propres à un client (détaillant ou grossiste) se gèrent désormais dans les accords commerciaux : validation par une seconde personne, contrôle de marge minimale et historique complet. Les anciens tarifs clients ne sont plus appliqués.
    </Alert>
  );
}

export function PageTarifs() {
  const [onglet, setOnglet] = useState<Onglet>('base');
  const { peut } = useSession();
  const gerer = peut('tarif.gerer');
  return (
    <>
      <EntetePage titre="Tarification" sousTitre="Ordre de résolution : accord commercial actif → promotion éligible → tarif du groupe ou prix de base, puis remise volume. Toute modification exige une vérification récente." />
      <Tabs<Onglet> label="Tarification" actif={onglet} onChange={setOnglet} onglets={[
        { id: 'base', libelle: 'Prix de base' }, { id: 'groupes', libelle: 'Groupes' }, { id: 'clients', libelle: 'Prix par client' },
        { id: 'remises', libelle: 'Remises volume' }, { id: 'promotions', libelle: 'Promotions' }, { id: 'simulateur', libelle: 'Simulateur' },
      ]} />
      <div role="tabpanel">
        {onglet === 'base' && <OngletPrixBase gerer={gerer} />}
        {onglet === 'groupes' && <OngletGroupes gerer={gerer} />}
        {onglet === 'clients' && <OngletPrixClients />}
        {onglet === 'remises' && <OngletRemises gerer={gerer} />}
        {onglet === 'promotions' && <OngletPromotions gerer={gerer} />}
        {onglet === 'simulateur' && <OngletSimulateur />}
      </div>
    </>
  );
}

function OngletPrixBase({ gerer }: { gerer: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { nom } = useNomProduit();
  const [filtre, setFiltre] = useState('');
  const [f, setF] = useState({ produitId: '', conditionnementType: 'PALETTE', prix: '', valideDu: aujourdhuiIso() });
  const q = useQuery({ queryKey: ['admin', 'tarifs', 'prix-base', filtre], queryFn: () => get<Page<S<'PrixBase'>>>('/admin/tarifs/prix-base', { produitId: filtre, taille: 100 }) });
  const m = useMutation({
    mutationFn: () => post('/admin/tarifs/prix-base', { produitId: f.produitId, conditionnementType: f.conditionnementType, prixHtCentimes: versCentimes(f.prix), valideDu: f.valideDu }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin', 'tarifs'] }); setF((x) => ({ ...x, prix: '' })); toast({ ton: 'succes', titre: 'Prix de base enregistré', texte: 'La période précédente a été clôturée.' }); },
  });
  return (
    <div className="my-pile">
      {gerer && (
        <Card as="section" aria-labelledby="t-npb">
          <h2 id="t-npb" className="my-titre-4">Nouveau prix de base</h2>
          <form className="my-form-ligne" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', alignItems: 'end', marginTop: 12 }} onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
            <ChoixProduit valeur={f.produitId} onChange={(v) => setF({ ...f, produitId: v })} />
            <SelectField label="Conditionnement" value={f.conditionnementType} onChange={(e) => setF({ ...f, conditionnementType: e.target.value })} options={CONDS} />
            <TextField label="Prix HT (€)" inputMode="decimal" required value={f.prix} onChange={(e) => setF({ ...f, prix: e.target.value })} erreur={m.error instanceof ApiErreur ? m.error.champs.prixHtCentimes : undefined} />
            <TextField label="Valable à partir du" type="date" required value={f.valideDu} onChange={(e) => setF({ ...f, valideDu: e.target.value })} />
            <Button type="submit" icone={Plus} chargement={m.isPending} disabled={!f.produitId || !f.prix}>Enregistrer</Button>
          </form>
          <ErreurFormulaire erreur={m.error} />
        </Card>
      )}
      <div style={{ maxWidth: 420 }}><ChoixProduit valeur={filtre} onChange={setFiltre} /></div>
      <EtatRequete requete={q} vide={(d) => d.total === 0} titreVide="Aucun prix de base" enfants={(d) => (
        <Table<S<'PrixBase'>> legende="Prix de base" lignes={d.donnees} cle={(x) => x.id!} colonnes={[
          { cle: 'p', titre: 'Produit', rendu: (x) => nom(x.produitId) },
          { cle: 'c', titre: 'Conditionnement', rendu: (x) => LIBELLE_CONDITIONNEMENT[x.conditionnementType!] },
          { cle: 'm', titre: 'Prix HT', num: true, rendu: (x) => euros(x.prixHtCentimes) },
          { cle: 'v', titre: 'Validité', rendu: (x) => `${x.valideDu}${x.valideAu ? ` → ${x.valideAu}` : ' → en cours'}` },
        ]} />
      )} />
    </div>
  );
}

function OngletGroupes({ gerer }: { gerer: boolean }) {
  const qc = useQueryClient();
  const groupes = useGroupes();
  const { nom } = useNomProduit();
  const [choisi, setChoisi] = useState('');
  const [ng, setNg] = useState({ code: '', libelle: '' });
  const [t, setT] = useState({ produitId: '', conditionnementType: 'PALETTE', mode: 'remise', valeur: '', valideDu: aujourdhuiIso() });
  const tarifs = useQuery({ queryKey: ['admin', 'tarifs', 'groupe', choisi], queryFn: () => get<S<'TarifGroupe'>[]>(`/admin/tarifs/groupes/${choisi}/tarifs`), enabled: !!choisi });
  const creer = useMutation({ mutationFn: () => post('/admin/tarifs/groupes', { code: ng.code.trim().toUpperCase(), libelle: ng.libelle.trim() }), onSuccess: () => { setNg({ code: '', libelle: '' }); void qc.invalidateQueries({ queryKey: ['admin', 'tarifs', 'groupes'] }); } });
  const ajouter = useMutation({
    mutationFn: () => post(`/admin/tarifs/groupes/${choisi}/tarifs`, { produitId: t.produitId, conditionnementType: t.conditionnementType, valideDu: t.valideDu, ...(t.mode === 'remise' ? { remisePourcentBp: Math.round(Number(t.valeur.replace(',', '.')) * 100) } : { prixHtCentimes: versCentimes(t.valeur) }) }),
    onSuccess: () => { setT((x) => ({ ...x, valeur: '' })); void qc.invalidateQueries({ queryKey: ['admin', 'tarifs', 'groupe', choisi] }); },
  });
  return (
    <div className="my-pile">
      <EtatRequete requete={groupes} vide={(d) => !d.length} titreVide="Aucun groupe" enfants={(d) => (
        <Table legende="Groupes tarifaires" lignes={d} cle={(g) => g.id} onLigne={(g) => setChoisi(g.id)} colonnes={[
          { cle: 'c', titre: 'Code', rendu: (g) => g.code },
          { cle: 'l', titre: 'Libellé', rendu: (g) => g.libelle },
          { cle: 'n', titre: 'Clients', num: true, rendu: (g) => g.nbEntreprises ?? 0 },
          { cle: 'a', titre: <span className="my-sr">Actions</span>, actions: true, rendu: (g) => <Button compact variante={choisi === g.id ? 'primaire' : 'secondaire'} onClick={() => setChoisi(g.id)}>Tarifs du groupe</Button> },
        ]} />
      )} />
      {gerer && (
        <form className="my-ligne" onSubmit={(e) => { e.preventDefault(); creer.mutate(); }}>
          <TextField label="Code du nouveau groupe" value={ng.code} onChange={(e) => setNg({ ...ng, code: e.target.value })} />
          <TextField label="Libellé" value={ng.libelle} onChange={(e) => setNg({ ...ng, libelle: e.target.value })} />
          <Button type="submit" icone={Plus} chargement={creer.isPending} disabled={!ng.code || !ng.libelle}>Créer le groupe</Button>
        </form>
      )}
      <ErreurFormulaire erreur={creer.error} />
      {choisi && (
        <Card as="section" aria-labelledby="t-tg">
          <h2 id="t-tg" className="my-titre-4">Tarifs du groupe {groupes.data?.find((g) => g.id === choisi)?.code}</h2>
          {gerer && (
            <form className="my-form-ligne" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', alignItems: 'end', margin: '12px 0' }} onSubmit={(e) => { e.preventDefault(); ajouter.mutate(); }}>
              <ChoixProduit valeur={t.produitId} onChange={(v) => setT({ ...t, produitId: v })} />
              <SelectField label="Conditionnement" value={t.conditionnementType} onChange={(e) => setT({ ...t, conditionnementType: e.target.value })} options={CONDS} />
              <SelectField label="Type" value={t.mode} onChange={(e) => setT({ ...t, mode: e.target.value })} options={[{ value: 'remise', label: 'Remise (%)' }, { value: 'prix', label: 'Prix fixe HT (€)' }]} />
              <TextField label={t.mode === 'remise' ? 'Remise (%)' : 'Prix HT (€)'} inputMode="decimal" value={t.valeur} onChange={(e) => setT({ ...t, valeur: e.target.value })} />
              <TextField label="À partir du" type="date" value={t.valideDu} onChange={(e) => setT({ ...t, valideDu: e.target.value })} />
              <Button type="submit" icone={Plus} chargement={ajouter.isPending} disabled={!t.produitId || !t.valeur}>Ajouter</Button>
            </form>
          )}
          <ErreurFormulaire erreur={ajouter.error} />
          <EtatRequete requete={tarifs} vide={(d) => !d.length} titreVide="Aucun tarif spécifique" enfants={(d) => (
            <Table<S<'TarifGroupe'>> legende="Tarifs du groupe" lignes={d} cle={(x) => x.id!} colonnes={[
              { cle: 'p', titre: 'Produit', rendu: (x) => nom(x.produitId) },
              { cle: 'c', titre: 'Conditionnement', rendu: (x) => LIBELLE_CONDITIONNEMENT[x.conditionnementType!] },
              { cle: 'v', titre: 'Valeur', num: true, rendu: (x) => (x.prixHtCentimes != null ? euros(x.prixHtCentimes) : `− ${pourcentBp(x.remisePourcentBp)}`) },
              { cle: 'd', titre: 'Validité', rendu: (x) => `${x.valideDu}${x.valideAu ? ` → ${x.valideAu}` : ''}` },
            ]} />
          )} />
        </Card>
      )}
    </div>
  );
}

function OngletRemises({ gerer }: { gerer: boolean }) {
  const qc = useQueryClient();
  const cats = useCategories();
  const { nom } = useNomProduit();
  const [f, setF] = useState({ portee: 'GLOBALE', cibleId: '', conditionnementType: 'PALETTE', seuil: '', remise: '' });
  const q = useQuery({ queryKey: ['admin', 'tarifs', 'remises'], queryFn: () => get<S<'RemiseVolume'>[]>('/admin/tarifs/remises-volume') });
  const inval = () => void qc.invalidateQueries({ queryKey: ['admin', 'tarifs', 'remises'] });
  const creer = useMutation({ mutationFn: () => post('/admin/tarifs/remises-volume', { portee: f.portee, ...(f.portee !== 'GLOBALE' ? { cibleId: f.cibleId } : {}), conditionnementType: f.conditionnementType, seuilQuantite: Number(f.seuil), remiseBp: Math.round(Number(f.remise.replace(',', '.')) * 100) }), onSuccess: () => { setF((x) => ({ ...x, seuil: '', remise: '' })); inval(); } });
  const suppr = useMutation({ mutationFn: (r: S<'RemiseVolume'>) => del(`/admin/tarifs/remises-volume/${r.id}`, { version: r.version }), onSuccess: inval });
  const cible = (r: S<'RemiseVolume'>) => (r.portee === 'GLOBALE' ? 'Tous produits' : r.portee === 'CATEGORIE' ? cats.data?.find((c) => c.id === r.cibleId)?.libelle ?? 'Catégorie' : nom(r.cibleId));
  return (
    <div className="my-pile">
      {gerer && (
        <form className="my-form-ligne" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', alignItems: 'end' }} onSubmit={(e) => { e.preventDefault(); creer.mutate(); }}>
          <SelectField label="Portée" value={f.portee} onChange={(e) => setF({ ...f, portee: e.target.value, cibleId: '' })} options={[{ value: 'GLOBALE', label: 'Tous produits' }, { value: 'CATEGORIE', label: 'Catégorie' }, { value: 'PRODUIT', label: 'Produit' }]} />
          {f.portee === 'CATEGORIE' && <SelectField label="Catégorie" value={f.cibleId} onChange={(e) => setF({ ...f, cibleId: e.target.value })} options={[{ value: '', label: 'Choisir…' }, ...(cats.data ?? []).map((c) => ({ value: c.id, label: c.libelle }))]} />}
          {f.portee === 'PRODUIT' && <ChoixProduit valeur={f.cibleId} onChange={(v) => setF({ ...f, cibleId: v })} />}
          <SelectField label="Conditionnement" value={f.conditionnementType} onChange={(e) => setF({ ...f, conditionnementType: e.target.value })} options={CONDS} />
          <TextField label="À partir de (quantité)" inputMode="numeric" value={f.seuil} onChange={(e) => setF({ ...f, seuil: e.target.value })} />
          <TextField label="Remise (%)" inputMode="decimal" value={f.remise} onChange={(e) => setF({ ...f, remise: e.target.value })} />
          <Button type="submit" icone={Plus} chargement={creer.isPending} disabled={!f.seuil || !f.remise}>Ajouter</Button>
        </form>
      )}
      <ErreurFormulaire erreur={creer.error ?? suppr.error} />
      <EtatRequete requete={q} vide={(d) => !d.length} titreVide="Aucune remise volume" enfants={(d) => (
        <Table<S<'RemiseVolume'>> legende="Remises volume" lignes={d} cle={(x) => x.id!} colonnes={[
          { cle: 'p', titre: 'Portée', rendu: cible },
          { cle: 'c', titre: 'Conditionnement', rendu: (x) => LIBELLE_CONDITIONNEMENT[x.conditionnementType!] },
          { cle: 's', titre: 'À partir de', num: true, rendu: (x) => x.seuilQuantite },
          { cle: 'r', titre: 'Remise', num: true, rendu: (x) => pourcentBp(x.remiseBp) },
          ...(gerer ? [{ cle: 'a', titre: <span className="my-sr">Actions</span>, actions: true, rendu: (x: S<'RemiseVolume'>) => <Button compact variante="tertiaire" icone={Trash2} onClick={() => suppr.mutate(x)} aria-label={`Supprimer la remise ${cible(x)} ${x.seuilQuantite}`}>Supprimer</Button> }] : []),
        ]} />
      )} />
    </div>
  );
}

function OngletPromotions({ gerer }: { gerer: boolean }) {
  const qc = useQueryClient();
  const { nom } = useNomProduit();
  const [f, setF] = useState({ produitId: '', conditionnementType: 'PALETTE', prix: '', du: aujourdhuiIso(), au: '' });
  const q = useQuery({ queryKey: ['admin', 'tarifs', 'promotions'], queryFn: () => get<S<'Promotion'>[]>('/admin/tarifs/promotions') });
  const creer = useMutation({ mutationFn: () => post('/admin/tarifs/promotions', { produitId: f.produitId, conditionnementType: f.conditionnementType, prixPromoCentimes: versCentimes(f.prix), du: f.du, au: f.au }), onSuccess: () => { setF((x) => ({ ...x, prix: '' })); void qc.invalidateQueries({ queryKey: ['admin', 'tarifs', 'promotions'] }); } });
  return (
    <div className="my-pile">
      {gerer && (
        <form className="my-form-ligne" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', alignItems: 'end' }} onSubmit={(e) => { e.preventDefault(); creer.mutate(); }}>
          <ChoixProduit valeur={f.produitId} onChange={(v) => setF({ ...f, produitId: v })} />
          <SelectField label="Conditionnement" value={f.conditionnementType} onChange={(e) => setF({ ...f, conditionnementType: e.target.value })} options={CONDS} />
          <TextField label="Prix promotionnel HT (€)" inputMode="decimal" value={f.prix} onChange={(e) => setF({ ...f, prix: e.target.value })} />
          <TextField label="Du" type="date" value={f.du} onChange={(e) => setF({ ...f, du: e.target.value })} />
          <TextField label="Au" type="date" value={f.au} onChange={(e) => setF({ ...f, au: e.target.value })} />
          <Button type="submit" icone={Plus} chargement={creer.isPending} disabled={!f.produitId || !f.prix || !f.au}>Ajouter</Button>
        </form>
      )}
      <ErreurFormulaire erreur={creer.error} />
      <EtatRequete requete={q} vide={(d) => !d.length} titreVide="Aucune promotion" enfants={(d) => (
        <Table<S<'Promotion'>> legende="Promotions" lignes={d} cle={(x) => x.id!} colonnes={[
          { cle: 'p', titre: 'Produit', rendu: (x) => nom(x.produitId) },
          { cle: 'c', titre: 'Conditionnement', rendu: (x) => LIBELLE_CONDITIONNEMENT[x.conditionnementType!] },
          { cle: 'm', titre: 'Prix promo HT', num: true, rendu: (x) => euros(x.prixPromoCentimes) },
          { cle: 'd', titre: 'Période', rendu: (x) => `${x.du} → ${x.au}` },
        ]} />
      )} />
    </div>
  );
}

function OngletSimulateur() {
  const clients = useClientsSelecteur();
  const { nom } = useNomProduit();
  const [f, setF] = useState({ entrepriseId: '', produitId: '', conditionnementType: 'PALETTE', quantite: '1', date: aujourdhuiIso() });
  const m = useMutation({ mutationFn: () => post<S<'CalculPrixReponse'> & { lignes: (S<'LigneCalculee'> & { prixTrouve?: boolean })[] }>('/admin/tarifs/simuler', { entrepriseId: f.entrepriseId, date: f.date, lignes: [{ produitId: f.produitId, conditionnementType: f.conditionnementType, quantite: Number(f.quantite) }] }) });
  const l = m.data?.lignes[0];
  return (
    <Card as="section" aria-labelledby="t-sim">
      <h2 id="t-sim" className="my-titre-4">Simuler le prix d’un client</h2>
      <form className="my-form-ligne" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', alignItems: 'end', marginTop: 12 }} onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        <SelectField label="Client" value={f.entrepriseId} onChange={(e) => setF({ ...f, entrepriseId: e.target.value })} options={[{ value: '', label: 'Choisir…' }, ...(clients.data?.donnees ?? []).map((c) => ({ value: c.id, label: `${c.reference} — ${c.raisonSociale}` }))]} />
        <ChoixProduit valeur={f.produitId} onChange={(v) => setF({ ...f, produitId: v })} />
        <SelectField label="Conditionnement" value={f.conditionnementType} onChange={(e) => setF({ ...f, conditionnementType: e.target.value })} options={CONDS} />
        <TextField label="Quantité" inputMode="numeric" value={f.quantite} onChange={(e) => setF({ ...f, quantite: e.target.value })} />
        <TextField label="Date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <Button type="submit" icone={Calculator} chargement={m.isPending} disabled={!f.entrepriseId || !f.produitId}>Calculer</Button>
      </form>
      <ErreurFormulaire erreur={m.error} />
      {l && (
        <div style={{ marginTop: 16 }} data-testid="resultat-simulation">
          {l.prixTrouve === false ? <Alert ton="alerte">Aucun prix applicable pour ce conditionnement à cette date.</Alert> : (
            <dl className="my-dl">
              <dt>Produit</dt><dd>{nom(l.produitId)}</dd>
              <dt>Prix unitaire HT</dt><dd className="my-prix">{euros(l.prixUnitaireHtCentimes)} {l.prixBarreCentimes ? <span className="my-prix-barre">{euros(l.prixBarreCentimes)}</span> : null}</dd>
              <dt>Source</dt><dd>{l.source === 'CLIENT' ? 'Tarif spécifique client' : l.source === 'GROUPE' ? 'Tarif du groupe' : 'Prix de base'}</dd>
              <dt>Remise volume</dt><dd>{pourcentBp(l.remiseBp)}</dd>
              <dt>Total HT</dt><dd className="my-num">{euros(l.totalHtCentimes)}</dd>
            </dl>
          )}
        </div>
      )}
    </Card>
  );
}
